import { useState } from 'react'
import { useRules, useCreateRule, useDeleteRule } from '../api/hooks'
import type { ConditionType, Severity } from '../api/client'

const CONDITION_TYPES: ConditionType[] = ['offline', 'value_threshold', 'battery_low', 'anomaly']
const SEVERITIES: Severity[]           = ['warning', 'critical', 'info']

const SEVERITY_STYLES: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-700',
  warning:  'bg-yellow-100 text-yellow-700',
  info:     'bg-blue-100 text-blue-700',
}

export default function RulesPage() {
  const { data, isLoading, isError, error } = useRules()
  const deleteRule  = useDeleteRule()
  const [showForm, setShowForm] = useState(false)

  async function handleDelete(id: number) {
    if (!confirm('Delete this rule?')) return
    deleteRule.mutate(id)
  }

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Alert Rules</h1>
          <p className="text-sm text-gray-400 mt-0.5">Trigger notifications when conditions are met</p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="text-sm px-3 py-1.5 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors font-medium"
        >
          {showForm ? 'Cancel' : '+ New rule'}
        </button>
      </div>

      {showForm && (
        <CreateRuleForm onCreated={() => setShowForm(false)} />
      )}

      {isLoading && (
        <div className="text-sm text-gray-400 py-12 text-center">Loading rules…</div>
      )}

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error instanceof Error ? error.message : 'Failed to load rules'}
        </div>
      )}

      {data && data.rules.length === 0 && !showForm && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-sm font-medium">No alert rules yet</p>
          <p className="text-xs mt-1">Create a rule to get notified when a device goes offline or a value crosses a threshold.</p>
        </div>
      )}

      {data && data.rules.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Name', 'Condition', 'Severity', 'Cooldown', 'Status', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.rules.map(r => (
                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{r.condition_type}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_STYLES[r.severity]}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{r.cooldown_sec}s</td>
                  <td className="px-4 py-3">
                    {r.enabled
                      ? <span className="text-green-600 text-xs font-medium">● Active</span>
                      : <span className="text-gray-400 text-xs font-medium">⏸ Paused</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(r.id)}
                      disabled={deleteRule.isPending}
                      className="text-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-40"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CreateRuleForm({ onCreated }: { onCreated: () => void }) {
  const createRule = useCreateRule()
  const [name, setName]         = useState('')
  const [condType, setCondType] = useState<ConditionType>('offline')
  const [severity, setSeverity] = useState<Severity>('warning')
  const [cooldown, setCooldown] = useState(300)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    createRule.mutate(
      { name, condition_type: condType, severity, cooldown_sec: cooldown },
      { onSuccess: onCreated },
    )
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-800 mb-4">New alert rule</h2>
      <form onSubmit={submit} className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-500 mb-1">Rule name</label>
          <input
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            placeholder="Device offline"
          />
        </div>
        <div className="w-44">
          <label className="block text-xs font-medium text-gray-500 mb-1">Condition</label>
          <select
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={condType}
            onChange={e => setCondType(e.target.value as ConditionType)}
          >
            {CONDITION_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="w-36">
          <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
          <select
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={severity}
            onChange={e => setSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="w-32">
          <label className="block text-xs font-medium text-gray-500 mb-1">Cooldown (s)</label>
          <input
            type="number"
            min={60}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={cooldown}
            onChange={e => setCooldown(Number(e.target.value))}
          />
        </div>
        <button
          type="submit"
          disabled={createRule.isPending}
          className="text-sm px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors font-medium disabled:opacity-40"
        >
          {createRule.isPending ? 'Saving…' : 'Create rule'}
        </button>
      </form>
      {createRule.isError && (
        <p className="text-xs text-red-600 mt-3">
          {createRule.error instanceof Error ? createRule.error.message : 'Failed to create rule'}
        </p>
      )}
    </div>
  )
}
